import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { ChevronRight, Send, CheckCircle, Loader2, Upload, X } from "lucide-react";

export default function DynamicForm({ formKey, previewFormId, fallback }) {
  const params = useParams();
  const key = formKey || params.key;
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const T = (fr, en) => (lang === "fr" ? fr : en);

  const [config, setConfig] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState({});
  const [hp, setHp] = useState("");
  const [refs, setRefs] = useState({ countries: [], categories: [], channels: [] });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const url = previewFormId
      ? `${API}/admin/forms/${previewFormId}/preview`
      : `${API}/public/forms/${key}`;
    const opts = previewFormId ? { withCredentials: true } : {};
    axios.get(url, opts)
      .then((r) => setConfig(r.data))
      .catch(() => setNotFound(true));
    axios.get(`${API}/countries`).then((r) => setRefs((p) => ({ ...p, countries: Array.isArray(r.data) ? r.data : [] }))).catch(() => {});
    axios.get(`${API}/categories`).then((r) => setRefs((p) => ({ ...p, categories: Array.isArray(r.data) ? r.data : [] }))).catch(() => {});
  }, [key, previewFormId]);

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (previewFormId) { return; }  // preview mode: no submission
    setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/public/forms/${key}/submit`, { values, hp_field: hp });
      setSent(true); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setError(e2.response?.data?.detail || T("Une erreur est survenue.", "Something went wrong."));
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-[#0F172A] border border-white/[0.06] text-white text-sm placeholder-[#94A3B8]/40 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/30";

  if (notFound) {
    if (fallback) return fallback;
    return (
      <div className="min-h-screen bg-[#050814] flex items-center justify-center" data-testid="dynamic-form-notfound">
        <div className="text-center"><p className="text-white text-lg mb-2">{T("Formulaire indisponible", "Form not available")}</p><Link to="/" className="text-[#FFC928] text-sm hover:underline">{T("Retour", "Back home")}</Link></div>
      </div>
    );
  }
  if (!config) return <div className="min-h-screen bg-[#050814] flex items-center justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;

  return (
    <div className="min-h-screen bg-[#050814]" data-testid="dynamic-form-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        {previewFormId && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-300" data-testid="preview-banner">
            {T("Aperçu du brouillon (version ", "Draft preview (version ")}{config.version}) — {T("non soumis", "not submitted")}
          </div>
        )}
        <nav className="flex items-center gap-2 text-sm text-[#94A3B8] mb-8">
          <Link to="/" className="hover:text-white">{T("Accueil", "Home")}</Link><ChevronRight size={14} />
          <span className="text-[#FFC928]">{T(config.title_fr, config.title_en)}</span>
        </nav>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "Poppins" }} data-testid="dynamic-form-title">
          {T(config.title_fr, config.title_en)}
        </motion.h1>
        {(config.description_en || config.description_fr) && <p className="text-[#94A3B8] text-sm mb-8">{T(config.description_fr, config.description_en)}</p>}

        {sent ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-green-500/20 bg-green-500/5 p-8 text-center" data-testid="dynamic-form-success">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
            <p className="text-white font-semibold text-lg">{T(config.success_fr, config.success_en)}</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="dynamic-form">
            <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
            {config.fields.map((f) => {
              if (f.type === "hidden") return null;
              const label = T(f.label_fr, f.label_en) || f.key;
              const ph = T(f.placeholder_fr, f.placeholder_en);
              const help = T(f.help_fr, f.help_en);
              const common = { "data-testid": `df-${f.key}`, className: inputCls, required: f.required, placeholder: ph };
              return (
                <div key={f.id}>
                  {!["consent", "checkbox"].includes(f.type) && <label className="text-xs font-medium text-[#94A3B8] mb-1.5 block">{label}{f.required && " *"}</label>}
                  {["short_text", "phone"].includes(f.type) && <input {...common} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />}
                  {f.type === "email" && <input type="email" {...common} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />}
                  {f.type === "url" && <input type="url" {...common} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />}
                  {f.type === "number" && <input type="number" {...common} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />}
                  {f.type === "date" && <input type="date" {...common} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />}
                  {f.type === "long_text" && <textarea rows={4} {...common} className={inputCls + " resize-none"} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />}
                  {["select", "radio"].includes(f.type) && (
                    <select {...common} className={inputCls + " cursor-pointer"} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}>
                      <option value="" className="bg-[#0F172A]">{T("Sélectionner…", "Select…")}</option>
                      {(f.options || []).map((o) => <option key={o.value} value={o.value} className="bg-[#0F172A]">{T(o.label_fr, o.label_en) || o.value}</option>)}
                    </select>
                  )}
                  {f.type === "country" && (
                    <select {...common} className={inputCls + " cursor-pointer"} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}>
                      <option value="" className="bg-[#0F172A]">{T("Sélectionner…", "Select…")}</option>
                      {refs.countries.map((c) => <option key={c.id} value={c.id} className="bg-[#0F172A]">{c.name}</option>)}
                    </select>
                  )}
                  {f.type === "category" && (
                    <select {...common} className={inputCls + " cursor-pointer"} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}>
                      <option value="" className="bg-[#0F172A]">{T("Sélectionner…", "Select…")}</option>
                      {refs.categories.map((c) => <option key={c.id} value={c.id} className="bg-[#0F172A]">{c.name_en || c.name}</option>)}
                    </select>
                  )}
                  {["file", "image", "channel", "multi_select"].includes(f.type) && (
                    <FileOrText field={f} formKey={key} value={values[f.key]} onChange={(v) => set(f.key, v)} refs={refs} T={T} inputCls={inputCls} />
                  )}
                  {["consent", "checkbox"].includes(f.type) && (
                    <label className="flex items-start gap-3 cursor-pointer" data-testid={`df-${f.key}`}>
                      <input type="checkbox" required={f.required} className="mt-1 accent-[#FFC928]" checked={!!values[f.key]} onChange={(e) => set(f.key, e.target.checked)} />
                      <span className="text-sm text-[#A1A9B8]">{label}{f.required && " *"}</span>
                    </label>
                  )}
                  {help && <p className="text-xs text-[#94A3B8]/60 mt-1">{help}</p>}
                </div>
              );
            })}
            {error && <div className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-400" data-testid="df-error">{error}</div>}
            <button type="submit" disabled={submitting} data-testid="df-submit" className="flex items-center gap-2 px-8 py-3 bg-[#FFC928] text-black font-bold text-sm rounded-xl hover:bg-[#FFD54F] transition-all disabled:opacity-60">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {T(config.submit_fr, config.submit_en)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function FileOrText({ field, formKey, value, onChange, refs, T, inputCls }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  if (field.type === "channel" || field.type === "multi_select") {
    return (
      <input className={inputCls} data-testid={`df-${field.key}`} value={value || ""} placeholder={T(field.placeholder_fr, field.placeholder_en)} onChange={(e) => onChange(e.target.value)} />
    );
  }
  const kind = field.type === "file" ? "document" : "image";
  const handle = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/public/forms/${formKey}/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch { /* ignore */ } finally { setUploading(false); }
  };
  return (
    <div>
      {value ? (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#0F172A] border border-[#FFC928]/30">
          <span className="flex-1 text-sm text-white truncate">Uploaded</span>
          <button type="button" onClick={() => onChange("")} className="text-[#94A3B8] hover:text-red-400"><X size={16} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`df-${field.key}`}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0F172A] border border-dashed border-white/[0.15] text-[#94A3B8] text-sm hover:border-[#FFC928]/40">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {T("Téléverser", "Upload")}
        </button>
      )}
      <input ref={inputRef} type="file" className="hidden" accept={kind === "document" ? "application/pdf" : "image/*"} onChange={handle} />
    </div>
  );
}
